// src/components/Dashboard/Account/Account.js
import React, { useState, useEffect } from 'react';
import './Account.css';
import { useLanguage } from '../../../context/LanguageContext';
import { useUser } from '../../../context/UserContext';
import { getTranslation } from '../../../i18n/translations';

const Account = ({ onNavigateToDeposit }) => {
  const { language } = useLanguage();
  const { user } = useUser();
  
  // Dynamic performance score state
  const [performanceScore, setPerformanceScore] = useState(75);
  const [scoreDirection, setScoreDirection] = useState(1); // 1 for increasing, -1 for decreasing
  
  // Automated metrics state
  const [hashrate, setHashrate] = useState(user.totalMiningPower || 142.5);
  const [uptime, setUptime] = useState(99.8);
  const [efficiency, setEfficiency] = useState(95.3);
  const [temperature, setTemperature] = useState(68);
  
  // Trend directions for all metrics
  const [hashrateDirection, setHashrateDirection] = useState(1);
  const [uptimeDirection, setUptimeDirection] = useState(1);
  const [efficiencyDirection, setEfficiencyDirection] = useState(1);
  const [temperatureDirection, setTemperatureDirection] = useState(-1);
  
  // Convert USD balance to BTC (assuming BTC price of $130,000)
  const btcBalance = user.balance / 130000;
  
  // Automated performance score and metrics animation
  useEffect(() => {
    const interval = setInterval(() => {
      // Update performance score
      setPerformanceScore(prevScore => { 
        let newScore = prevScore + (Math.random() * 4 - 2) * scoreDirection;
        
        if (newScore >= 95) {
          newScore = 95;
          setScoreDirection(-1);
        } else if (newScore <= 25) {
          newScore = 25;
          setScoreDirection(1);
        } else if (Math.random() < 0.1) {
          setScoreDirection(prev => prev * -1);
        }
        
        return Math.round(newScore);
      });
      
      // Update hashrate (120-180 TH/s)
      setHashrate(prevHashrate => {
        let newHashrate = prevHashrate + (Math.random() * 3 - 1.5) * hashrateDirection;
        
        if (newHashrate >= 180) {
          newHashrate = 180;
          setHashrateDirection(-1);
        } else if (newHashrate <= 120) {
          newHashrate = 120;
          setHashrateDirection(1);
        } else if (Math.random() < 0.15) {
          setHashrateDirection(prev => prev * -1);
        }
        
        return parseFloat(newHashrate.toFixed(1));
      });
      
      // Update uptime (96-99.9%)
      setUptime(prevUptime => {
        let newUptime = prevUptime + (Math.random() * 0.4 - 0.2) * uptimeDirection;
        
        if (newUptime >= 99.9) {
          newUptime = 99.9;
          setUptimeDirection(-1);
        } else if (newUptime <= 96) {
          newUptime = 96;
          setUptimeDirection(1);
        } else if (Math.random() < 0.1) {
          setUptimeDirection(prev => prev * -1);
        }
        
        return parseFloat(newUptime.toFixed(1));
      });
      
      // Update efficiency (88-98%)
      setEfficiency(prevEfficiency => {
        let newEfficiency = prevEfficiency + (Math.random() * 2 - 1) * efficiencyDirection;
        
        if (newEfficiency >= 98) {
          newEfficiency = 98;
          setEfficiencyDirection(-1);
        } else if (newEfficiency <= 88) {
          newEfficiency = 88;
          setEfficiencyDirection(1);
        } else if (Math.random() < 0.12) {
          setEfficiencyDirection(prev => prev * -1);
        }
        
        return parseFloat(newEfficiency.toFixed(1));
      });
      
      // Update temperature (55-75°C)
      setTemperature(prevTemp => {
        let newTemp = prevTemp + (Math.random() * 2 - 1) * temperatureDirection;
        
        if (newTemp >= 75) {
          newTemp = 75;
          setTemperatureDirection(-1);
        } else if (newTemp <= 55) {
          newTemp = 55;
          setTemperatureDirection(1);
        } else if (Math.random() < 0.1) {
          setTemperatureDirection(prev => prev * -1);
        }
        
        return Math.round(newTemp);
      });
    }, 2000); // Update every 2 seconds
    
    return () => clearInterval(interval);
  }, [scoreDirection, hashrateDirection, uptimeDirection, efficiencyDirection, temperatureDirection]);
  
  // Calculate circle stroke properties
  const circumference = 2 * Math.PI * 35; // radius = 35
  const strokeDashoffset = circumference - (performanceScore / 100) * circumference;
  
  // Get performance color and status
  const getPerformanceColor = (score) => {
    return score <= 50 ? '#ef4444' : '#10b981';
  };
  
  const getPerformanceStatus = (score) => {
    if (score <= 30) return 'Critical';
    if (score <= 50) return 'Poor';
    if (score <= 70) return 'Good';
    if (score <= 85) return 'Excellent';
    return 'Outstanding';
  };
  
  // Utility functions for all metrics
  const getMetricColor = (value, thresholds) => {
    const { good, warning } = thresholds;
    if (value >= good) return '#10b981';
    if (value >= warning) return '#f59e0b';
    return '#ef4444';
  };
  
  const getMetricTrend = (direction, value) => {
    const isPositive = direction > 0;
    const trendValue = (Math.random() * 2 + 0.1).toFixed(1);
    return {
      isPositive,
      value: trendValue,
      color: isPositive ? '#12b76a' : '#ef4444'
    };
  };
  
  // Define thresholds for each metric
  const metricThresholds = {
    hashrate: { good: 150, warning: 130 },
    uptime: { good: 99, warning: 97 },
    efficiency: { good: 93, warning: 90 },
    temperature: { good: 65, warning: 70 } // Lower is better for temperature
  };
  
  const getTemperatureColor = (temp) => {
    if (temp <= 65) return '#10b981';
    if (temp <= 70) return '#f59e0b';
    return '#ef4444';
  };
  
  return (
    <div className="account">
      <h2 className="account__title">{getTranslation('myAccount', language)}</h2>
      <div className="account__info">
        <div id="account-wallet" className="account__total account__total-width">
          <h2 className="account__total-title account__total-title_desktop">{getTranslation('totalBalance', language)}</h2>
          <div className="account__total-assets account__total-assets_background">
            <div className="account__total-assets-mobile-block">
              <h2 className="account__total-title account__total-title_mobile">{getTranslation('totalBalance', language)}</h2>
              <div className="account__total-balance">
                <div className="account__total-subtitle-block">
                  <div className="account__total-subtitle-group account__total-subtitle-group_mobile">
                    <p className="account__total-text">BTC:</p>
                    <div className="account__total-value-block">
                      <p className="account__total-value">{btcBalance.toFixed(8)} BTC</p>
                    </div>
                  </div>
                  <div className="account__total-subtitle-group account__total-subtitle-group_mobile">
                    <p className="account__total-text">USD:</p>
                    <div className="account__total-value-block">
                      <p className="account__total-value">$ {user.balance.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
                <div className="account__total-button-block">
                  
                  <div className="account__total-btn-block">
                    <button>
                       <a href="/general-settings/withdrawals" className="withdraw-button" style={{ flex: '1 1 0%' }}>
                    <button className="transparent account__total-button-grey" type="button">
                      {getTranslation('withdraw', language)}
                    </button>
                  </a>
                    </button>
                    <button 
                      className="transparent account__total-button-grey btc-button account__total-button-link" 
                      type="button"
                      style={{ flex: '1 1 0%' }}
                      onClick={() => onNavigateToDeposit && onNavigateToDeposit('BTC')}
                    >
                      {' '}+BTC{' '}
                    </button>
                    <button 
                      className="transparent account__total-button-grey usd-button account__total-button-link" 
                      type="button"
                      style={{ flex: '1 1 0%' }}
                      onClick={() => onNavigateToDeposit && onNavigateToDeposit('USD')}
                    >
                      {' '}+${' '}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="account__total_mobile-background account__total_desktop">
          <div className="account__total">
            <h2 className="account__total-title">{getTranslation('totalMiningPower', language)}</h2>
            <div className="account__total-assets account__total-assets_mobile account__total-assets_mobile-border">
              <div className="account__total-subtitle-group account__total-subtitle-group_padding">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="25"
                  height="25"
                  viewBox="0 0 25 25"
                  fill="none"
                >
                  <rect
                    x="0.775"
                    y="0.762793"
                    width="22.95"
                    height="22.95"
                    rx="5.475"
                    stroke="#1570EF"
                    strokeWidth="1.05"
                  />
                  <path
                    d="M13.1751 6.23779L7.83119 12.6505C7.6219 12.9016 7.51726 13.0272 7.51566 13.1333C7.51427 13.2255 7.55535 13.3132 7.62707 13.3711C7.70956 13.4378 7.87302 13.4378 8.19993 13.4378H12.5751L11.9751 18.2378L17.319 11.8251C17.5283 11.5739 17.633 11.4484 17.6346 11.3423C17.636 11.2501 17.5949 11.1624 17.5232 11.1045C17.4407 11.0378 17.2772 11.0378 16.9503 11.0378H12.5751L13.1751 6.23779Z"
                    stroke="#1570EF"
                    strokeWidth="1.05"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <p className="account__total-subtitle subtitle__total-subtitle_mobile">{user.totalMiningPower || 0}</p>
                <p className="account__total-subtitle_grey subtitle__total-subtitle_mobile">TH</p>
              </div>
              <p className="account__total-subtitle-text_null total-power-carrot-promos-account">
                {user.totalMiningPower > 0 ? `Daily earnings: $${user.dailyEarnings.toFixed(2)}` : getTranslation('nothingYet', language)}
              </p>
            </div>
          </div>
          <div className="account__total_mobile">
            <div className="account__total">
              <h2 className="account__total-title">{getTranslation('totalMiningReward', language)}</h2>
              <div className="account__total-assets account__total-assets_mobile">
                <div className="account__total-subtitle-group account__total-subtitle-group_padding">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="0.525"
                      y="0.525"
                      width="22.95"
                      height="22.95"
                      rx="5.475"
                      stroke="#1570EF"
                      strokeWidth="1.05"
                    />
                    <path
                      d="M9.75 11.4375C12.8566 11.4375 15.375 10.3042 15.375 8.90625C15.375 7.50828 12.8566 6.375 9.75 6.375C6.6434 6.375 4.125 7.50828 4.125 8.90625C4.125 10.3042 6.6434 11.4375 9.75 11.4375Z"
                      stroke="#1570EF"
                      strokeWidth="1.05"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M4.125 8.90625V11.7188C4.125 13.118 6.64219 14.25 9.75 14.25C12.8578 14.25 15.375 13.118 15.375 11.7188V8.90625"
                      stroke="#1570EF"
                      strokeWidth="1.05"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M7.5 11.2266V14.0391"
                      stroke="#1570EF"
                      strokeWidth="1.05"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M15.375 9.79944C17.9414 10.0385 19.875 11.058 19.875 12.2815C19.875 13.6807 17.3578 14.8127 14.25 14.8127C12.8719 14.8127 11.6062 14.5877 10.6289 14.2221"
                      stroke="#1570EF"
                      strokeWidth="1.05"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M8.625 14.2008V15.0938C8.625 16.493 11.1422 17.625 14.25 17.625C17.3578 17.625 19.875 16.493 19.875 15.0938V12.2812"
                      stroke="#1570EF"
                      strokeWidth="1.05"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M16.5 14.6016V17.4141"
                      stroke="#1570EF"
                      strokeWidth="1.05"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12 11.2266V17.4141"
                      stroke="#1570EF"
                      strokeWidth="1.05"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <p className="account__total-subtitle subtitle__total-subtitle_mobile">{(user.totalMiningReward / 130000).toFixed(8)}</p>
                  <p className="account__total-subtitle_grey subtitle__total-subtitle_mobile">BTC</p>
                </div>
                <p className="account__total-subtitle-text_null">
                  {user.totalMiningReward > 0 ? `Estimated total: $${user.totalMiningReward.toFixed(2)}` : getTranslation('wantSpecialDeal', language)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="account__total account__total_desktop">
          <h2 className="account__total-title">Total Mining Reward</h2>
          <div className="account__total-assets">
            <div className="account__total-subtitle-group account__total-subtitle-group_padding">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="0.525"
                  y="0.525"
                  width="22.95"
                  height="22.95"
                  rx="5.475"
                  stroke="#1570EF"
                  strokeWidth="1.05"
                />
                <path
                  d="M9.75 11.4375C12.8566 11.4375 15.375 10.3042 15.375 8.90625C15.375 7.50828 12.8566 6.375 9.75 6.375C6.6434 6.375 4.125 7.50828 4.125 8.90625C4.125 10.3042 6.6434 11.4375 9.75 11.4375Z"
                  stroke="#1570EF"
                  strokeWidth="1.05"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M4.125 8.90625V11.7188C4.125 13.118 6.64219 14.25 9.75 14.25C12.8578 14.25 15.375 13.118 15.375 11.7188V8.90625"
                  stroke="#1570EF"
                  strokeWidth="1.05"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M7.5 11.2266V14.0391"
                  stroke="#1570EF"
                  strokeWidth="1.05"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M15.375 9.79944C17.9414 10.0385 19.875 11.058 19.875 12.2815C19.875 13.6807 17.3578 14.8127 14.25 14.8127C12.8719 14.8127 11.6062 14.5877 10.6289 14.2221"
                  stroke="#1570EF"
                  strokeWidth="1.05"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8.625 14.2008V15.0938C8.625 16.493 11.1422 17.625 14.25 17.625C17.3578 17.625 19.875 16.493 19.875 15.0938V12.2812"
                  stroke="#1570EF"
                  strokeWidth="1.05"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M16.5 14.6016V17.4141"
                  stroke="#1570EF"
                  strokeWidth="1.05"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 11.2266V17.4141"
                  stroke="#1570EF"
                  strokeWidth="1.05"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <p className="account__total-subtitle subtitle__total-subtitle_mobile">{(user.totalMiningReward / 130000).toFixed(8)}</p>
              <p className="account__total-subtitle_grey subtitle__total-subtitle_mobile">BTC</p>
            </div>
            <p className="account__total-subtitle-text_null total-reward-carrot-deal-account">
              {user.totalMiningReward > 0 ? `Estimated total: $${user.totalMiningReward.toFixed(2)}` : 'Want a special deal? Contact your manager.'}
            </p>
          </div>
        </div>
      </div>
      
      <div className="account__analytics-widget">
        <div className="account__analytics-header">
          <div className="account__analytics-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M3 13l4 4L17 7"
                stroke="#1570EF"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle
                cx="12"
                cy="12"
                r="9"
                stroke="#1570EF"
                strokeWidth="2"
                fill="none"
              />
            </svg>
          </div>
          <div className="account__analytics-title-block">
            <h3 className="account__analytics-title">{getTranslation('miningAnalytics', language)}</h3>
            <p className="account__analytics-subtitle">{getTranslation('realTimePerformanceMetrics', language)}</p>
          </div>
        </div>
        
        <div className="account__analytics-metrics">
          <div className="account__analytics-metric-grid">
            <div className="account__analytics-metric">
              <div className="account__analytics-metric-header">
             
                <div className="account__analytics-metric-icon" 
                     style={{ background: `linear-gradient(135deg, ${getMetricColor(hashrate, metricThresholds.hashrate)}, ${getMetricColor(hashrate, metricThresholds.hashrate)}aa)` }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M13 2L3 14h8l-1 8 10-12h-8l1-8z"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
             
                <span className="account__analytics-metric-label">{getTranslation('hashrate', language)}</span>
              </div>
              <div className="account__analytics-metric-value">
                <span className="account__analytics-value" 
                style={{ color: getMetricColor(hashrate, metricThresholds.hashrate) }}>
                  {hashrate.toFixed(1)}
                </span>
                <span className="account__analytics-unit">TH/s</span>
              </div>
              <div className="account__analytics-metric-trend">
                <div className={`account__analytics-trend-${hashrateDirection > 0 ? 'positive' : 'negative'}`}>
                  <svg width="12" height="8" viewBox="0 0 12 8">
                    <path d={hashrateDirection > 0 ? "M1 7L6 2L11 7" : "M1 1L6 6L11 1"} 
                          stroke={hashrateDirection > 0 ? '#12b76a' : '#ef4444'} 
                          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span style={{ color: hashrateDirection > 0 ? '#12b76a' : '#ef4444' }}>
                    {hashrateDirection > 0 ? '+' : ''}{(Math.random() * 3 + 0.5).toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
            
            <div className="account__analytics-metric">
              <div className="account__analytics-metric-header">
                <div className="account__analytics-metric-icon" 
                     style={{ background: `linear-gradient(135deg, ${getMetricColor(uptime, metricThresholds.uptime)}, ${getMetricColor(uptime, metricThresholds.uptime)}aa)` }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="2"/>
                    <polyline points="12,6 12,12 16,14" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <span className="account__analytics-metric-label">{getTranslation('uptime', language)}</span>
              </div>
              <div className="account__analytics-metric-value">
                <span className="account__analytics-value" style={{ color: getMetricColor(uptime, metricThresholds.uptime) }}>
                  {uptime.toFixed(1)}
                </span>
                <span className="account__analytics-unit">%</span>
              </div>
              <div className="account__analytics-metric-trend">
                <div className={`account__analytics-trend-${uptimeDirection > 0 ? 'positive' : 'negative'}`}>
                  <svg width="12" height="8" viewBox="0 0 12 8">
                    <path d={uptimeDirection > 0 ? "M1 7L6 2L11 7" : "M1 1L6 6L11 1"} 
                          stroke={uptimeDirection > 0 ? '#12b76a' : '#ef4444'} 
                          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span style={{ color: uptimeDirection > 0 ? '#12b76a' : '#ef4444' }}>
                    {uptimeDirection > 0 ? '+' : ''}{(Math.random() * 0.5 + 0.1).toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
           <div className="account__analytics-metric">
              <div className="account__analytics-metric-header">
                <div className="account__analytics-metric-icon" 
                     style={{ background: `linear-gradient(135deg, ${getMetricColor(efficiency, metricThresholds.efficiency)}, ${getMetricColor(efficiency, metricThresholds.efficiency)}aa)` }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <line x1="12" y1="2" x2="12" y2="22" stroke="white" strokeWidth="2"/>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" stroke="white" strokeWidth="2"/>
                  </svg>
                </div>
                <span className="account__analytics-metric-label">{getTranslation('efficiency', language)}</span>
              </div>
              <div className="account__analytics-metric-value">
                <span className="account__analytics-value" style={{ color: getMetricColor(efficiency, metricThresholds.efficiency) }}>
                  {efficiency.toFixed(1)}
                </span>
                <span className="account__analytics-unit">%</span>
              </div>
              <div className="account__analytics-metric-trend">
                <div className={`account__analytics-trend-${efficiencyDirection > 0 ? 'positive' : 'negative'}`}>
                  <svg width="12" height="8" viewBox="0 0 12 8">
                    <path d={efficiencyDirection > 0 ? "M1 7L6 2L11 7" : "M1 1L6 6L11 1"} 
                          stroke={efficiencyDirection > 0 ? '#12b76a' : '#ef4444'} 
                          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span style={{ color: efficiencyDirection > 0 ? '#12b76a' : '#ef4444' }}>
                    {efficiencyDirection > 0 ? '+' : ''}{(Math.random() * 2 + 0.3).toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
            
            <div className="account__analytics-metric">
              <div className="account__analytics-metric-header">
                <div className="account__analytics-metric-icon" 
                     style={{ background: `linear-gradient(135deg, ${getTemperatureColor(temperature)}, ${getTemperatureColor(temperature)}aa)` }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M22 12h-4l-3 9L9 3l-3 9H2"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span className="account__analytics-metric-label">{getTranslation('temperature', language)}</span>
              </div>
              <div className="account__analytics-metric-value">
                <span className="account__analytics-value" style={{ color: getTemperatureColor(temperature) }}>
                  {temperature}
                </span>
                <span className="account__analytics-unit">°C</span>
              </div>
              <div className="account__analytics-metric-trend">
                <div className={`account__analytics-trend-${temperatureDirection < 0 ? 'positive' : 'negative'}`}>
                  <svg width="12" height="8" viewBox="0 0 12 8">
                    <path d={temperatureDirection < 0 ? "M1 7L6 2L11 7" : "M1 1L6 6L11 1"} 
                          stroke={temperatureDirection < 0 ? '#12b76a' : '#ef4444'} 
                          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span style={{ color: temperatureDirection < 0 ? '#12b76a' : '#ef4444' }}>
                    {temperatureDirection < 0 ? '-' : '+'}{(Math.random() * 2 + 0.5).toFixed(1)}°
                  </span>
                </div>
              </div>
            </div>
          
          </div>
          
        
        </div>
        
        <div className="account__analytics-actions">
          <div className="account__analytics-quick-actions">
            <h4 className="account__analytics-actions-title">{getTranslation('quickActions', language)}</h4>
            <div className="account__analytics-action-buttons">
              <a href="/rent-asics" className="account__analytics-action-button">
                <div className="account__analytics-action-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
                    <rect x="2" y="3" width="20" height="14" rx="2" stroke="#1570ef" strokeWidth="2"/>
                    <line x1="8" y1="21" x2="16" y2="21" stroke="#1570ef" strokeWidth="2"/>
                    <line x1="12" y1="17" x2="12" y2="21" stroke="#1570ef" strokeWidth="2"/>
                  </svg>
                </div>
                <span>{getTranslation('rentAsics', language)}</span>
              </a>
              
              <a href="/buy-asics" className="account__analytics-action-button">
                <div className="account__analytics-action-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
                    <circle cx="9" cy="21" r="1" stroke="#1570ef" strokeWidth="2"/>
                    <circle cx="20" cy="21" r="1" stroke="#1570ef" strokeWidth="2"/>
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" stroke="#1570ef" strokeWidth="2"/>
                  </svg>
                </div>
                <span>{getTranslation('buyAsics', language)}</span>
              </a>
              
              <a href="/dashboard" className="account__analytics-action-button">
                <div className="account__analytics-action-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="#1570ef" strokeWidth="2"/>
                    <polyline points="9,22 9,12 15,12 15,22" stroke="#1570ef" strokeWidth="2"/>
                  </svg>
                </div>
                <span>{getTranslation('overview', language)}</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Account;

